import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../styles/UISystem';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { CaretUpDown } from '@phosphor-icons/react/dist/ssr/CaretUpDown';
import { XCircle } from '@phosphor-icons/react/dist/ssr/XCircle';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { ButtonRounded } from '../UISystem/Buttons';
import { SelectionRow } from '../UISystem/Forms';
import { getProfileData } from '../../services/chat/profileService';
import { useConnectionContext, useConnectionData } from '../../contexts/ConnectionStateContext';
import { getAvailableSharingFields, hasValidPhoneDigits } from '../../utils/sharingFields';
import { commitSharedFields } from '../../services/sharing/commitSharedFields';
import { hasAboutMeContent } from '../../utils/profilePayload';

/**
 * SharingSelectionOverlay Component
 *
 * Simplified single-view design for Profile users to select and share contact information:
 *
 * Features:
 * - Category dropdown at top (Social, Work, Family, Other) with CaretUpDown icon
 * - Horizontal pill-shaped chips with icon + text inline
 * - Dynamic row layout based on available contacts
 * - Selected: Darker grey background (#C8C8C8), darker icon/text (#5A5A5A)
 * - Unselected: Light grey background (#F0F0F0), lighter icon/text (#9A9A9A)
 * - Phone pre-selected by default
 * - "About me" button + preview text, fixed row below contact list
 * - Full name toggle row (first name vs full name sharing)
 * - "Share" button sends immediately
 *
 * Icon order: Phone, Email, Instagram, LinkedIn, WhatsApp
 *
 * @param {string} chatRoomId - The chat room ID for this connection
 * @param {function} onClose - Callback when overlay closes
 * @param {function} onComplete - Callback when sharing is complete
 * @param {boolean} isInitialShare - Controls display mode:
 *   - true (default): Initial share during QR flow - single-select, name toggle, "Share" button
 *   - false: Update from chat "+" or detail page - multi-select, all contacts shown, pre-selected, "Update" button
 */

// ContactRow extracted to UISystem as SelectionRow — see src/components/UISystem/Forms/SelectionRow.js
// Field registry + display logic (formatPhoneNumber, hasValidPhoneDigits, getAvailableSharingFields)
// live in utils/sharingFields.js, shared with the inline SHARED list on the detail page.

// Single-select inline toggle row (Hometown, About me): icon shows only when on,
// label italic + dimmed when off. Local DRY of the two value-less single-select
// toggles — intentionally distinct from SelectionRow (multi-select), which always
// shows the icon and never italicizes.
const InlineToggleRow = ({ icon: Icon, iconWeight = 'regular', label, enabled, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 16px',
      backgroundColor: enabled ? '#F0F0F0' : '#F8F8F8',
      borderRadius: BORDER_RADIUS.PILL,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      width: '100%',
      overflow: 'hidden',
    }}
  >
    {enabled && <Icon size={20} color="#5A5A5A" weight={iconWeight} />}
    <span
      style={{
        fontSize: '16px',
        fontWeight: '400',
        fontStyle: enabled ? 'normal' : 'italic',
        color: enabled ? '#5A5A5A' : '#BEBEBE',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'left',
        flex: 1,
      }}
    >
      {label}
    </span>
  </div>
);

const SharingSelectionOverlay = ({
  chatRoomId,
  onClose,
  onComplete,
  isInitialShare = true,
  selectionMode = 'single', // 'single' = pick one contact, 'multi' = checkbox-style batch
  headerText = null, // Custom header text (e.g., "Share more")
  buttonText = null, // Override action button text (default: "Share"/"Update" based on isInitialShare)
  shareMethod = 'overlay', // Analytics trigger path — ProfileConnectionPage passes 'edit'
}) => {
  const { updateContactSharingStatus } = useConnectionContext();
  const connectionData = useConnectionData(chatRoomId);
  const [profileData, setProfileData] = useState(null);

  // Single-select mode: one contact key selected at a time
  const [selectedContact, setSelectedContact] = useState(null);
  // Name sharing toggle — overlay default is full name (user can toggle to first-name-only)
  const [nameSharingFull, setNameSharingFull] = useState(true);
  // About me is independent of contact selection (works in both modes)
  const [aboutMeEnabled, setAboutMeEnabled] = useState(false);
  // Hometown is its own shareable field — auto-selected (pre-checked) but shared only
  // on confirm — independent of contact selection and separate from the About me
  // bundle (see profile-hometown-sharing).
  const [hometownEnabled, setHometownEnabled] = useState(false);
  // First share flag — true when no prior shares exist for this connection
  const [isFirstShare, setIsFirstShare] = useState(false);

  // Multi-select mode: Track selection for each individual contact
  const [selectedFields, setSelectedFields] = useState({
    // Full name toggle (not a contact field — controls name sharing in multi-select)
    fullName: true,
    // Primary contacts
    phone: true,
    email: true,
    instagram: true,
    linkedin: true,
    whatsapp: true,
    // Secondary contacts
    phone2: true,
    email2: true,
    instagram2: true,
    whatsapp2: true,
    // Expanded contact types
    facebook: true,
    github: true,
    signal: true,
    snapchat: true,
    substack: true,
    telegram: true,
    tiktok: true,
    twitch: true,
    twitter: true,
    youtube: true,
    // Custom contact
    custom: true,
    // About me bundle (kept for multi-select mode compatibility)
    aboutMe: true, // Bundle: role, organization, whatIDo, currentCity, placesLived
  });
  // Condensed vs expanded contact list - initial share starts condensed, share more starts expanded
  const [isEditMode, setIsEditMode] = useState(!isInitialShare);

  // Load profile data and last selected group on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        console.log('[SharingSelection] Loading global Profile data');
        const data = await getProfileData();

        setProfileData(data);

        // Clean up orphaned greeting localStorage key (greeting feature removed)
        localStorage.removeItem('profile_greeting_text');

        // Restore nameSharing toggle — per-connection first, then global default
        const savedNameSharing = localStorage.getItem(`name_sharing_${chatRoomId}`);
        if (savedNameSharing) {
          setNameSharingFull(savedNameSharing === 'full');
        } else {
          const globalDefault = localStorage.getItem('profile_name_sharing');
          setNameSharingFull(globalDefault ? globalDefault === 'full' : true);
        }

        // Check for previous selections for this connection
        const sentSharedFieldsKey = `sent_shared_fields_${chatRoomId}`;
        const sentSharedFieldsStr = localStorage.getItem(sentSharedFieldsKey);

        if (sentSharedFieldsStr) {
          try {
            const sentSharedFields = JSON.parse(sentSharedFieldsStr);
            console.log('[SharingSelection] Loading previous selection:', sentSharedFields);

            // For single-select mode, restore the first contact from previous selection
            if (selectionMode === 'single') {
              // Find first contact (exclude aboutMe)
              const contactFields = sentSharedFields.filter(
                (f) => f !== 'aboutMe' && f !== 'photoUrl'
              );
              if (contactFields.length > 0) {
                setSelectedContact(contactFields[0]);
              }
              // Restore aboutMe + hometown state
              setAboutMeEnabled(sentSharedFields.includes('aboutMe'));
              setHometownEnabled(sentSharedFields.includes('currentCity'));
            } else {
              // Multi-select mode: restore all selections
              setAboutMeEnabled(sentSharedFields.includes('aboutMe'));
              setHometownEnabled(sentSharedFields.includes('currentCity'));
              setSelectedFields({
                // Full name — restore from per-connection nameSharing (already read above)
                fullName: savedNameSharing ? savedNameSharing === 'full' : true,
                // Primary contacts
                phone: sentSharedFields.includes('phone'),
                email: sentSharedFields.includes('email'),
                instagram: sentSharedFields.includes('instagram'),
                linkedin: sentSharedFields.includes('linkedin'),
                whatsapp: sentSharedFields.includes('whatsapp'),
                // Secondary contacts
                phone2: sentSharedFields.includes('phone2'),
                email2: sentSharedFields.includes('email2'),
                instagram2: sentSharedFields.includes('instagram2'),
                whatsapp2: sentSharedFields.includes('whatsapp2'),
                // Expanded contact types
                facebook: sentSharedFields.includes('facebook'),
                github: sentSharedFields.includes('github'),
                signal: sentSharedFields.includes('signal'),
                snapchat: sentSharedFields.includes('snapchat'),
                substack: sentSharedFields.includes('substack'),
                telegram: sentSharedFields.includes('telegram'),
                tiktok: sentSharedFields.includes('tiktok'),
                twitch: sentSharedFields.includes('twitch'),
                twitter: sentSharedFields.includes('twitter'),
                youtube: sentSharedFields.includes('youtube'),
                // Custom contact
                custom: sentSharedFields.includes('custom'),
                // About me bundle
                aboutMe: sentSharedFields.includes('aboutMe'),
              });
            }
          } catch (error) {
            console.error('[SharingSelection] Error parsing sent shared fields:', error);
          }
        } else if (selectionMode === 'single' && data) {
          // No per-connection selection - check global default from last session
          const globalDefault = localStorage.getItem('profile_default_contact');
          let contactSet = false;

          if (globalDefault && data[globalDefault]) {
            // Validate the global default still has data
            if (globalDefault.startsWith('phone') || globalDefault.startsWith('whatsapp')) {
              if (hasValidPhoneDigits(data[globalDefault])) {
                setSelectedContact(globalDefault);
                contactSet = true;
              }
            } else {
              setSelectedContact(globalDefault);
              contactSet = true;
            }
          }

          if (!contactSet) {
            // Fallback: pre-select first available contact by priority
            const priorityOrder = [
              'phone',
              'email',
              'instagram',
              'linkedin',
              'whatsapp',
              'phone2',
              'email2',
              'instagram2',
              'whatsapp2',
              'facebook',
              'github',
              'signal',
              'snapchat',
              'substack',
              'telegram',
              'tiktok',
              'twitch',
              'twitter',
              'youtube',
              'custom',
            ];
            for (const key of priorityOrder) {
              if (data[key]) {
                if (key.startsWith('phone') || key.startsWith('whatsapp')) {
                  if (hasValidPhoneDigits(data[key])) {
                    setSelectedContact(key);
                    break;
                  }
                } else {
                  setSelectedContact(key);
                  break;
                }
              }
            }
          }
          // About me defaults to ON if user has content — share what you've got
          // (hometown is no longer part of this bundle — it has its own default below)
          setAboutMeEnabled(hasAboutMeContent(data));
          // Hometown defaults ON when set — the coverage win (toggleable off)
          setHometownEnabled(!!data.currentCity);
        } else if (selectionMode === 'multi') {
          // No per-connection selection — pre-check from last connection's selections
          const globalFieldsStr = localStorage.getItem('profile_last_shared_fields');
          let globalFields = null;
          if (globalFieldsStr) {
            try {
              globalFields = JSON.parse(globalFieldsStr);
            } catch (e) {
              // ignore parse errors
            }
          }

          setSelectedFields((prev) => {
            const updated = {};
            Object.keys(prev).forEach((key) => {
              updated[key] = globalFields ? globalFields.includes(key) : false;
            });
            // fullName defaults based on global setting
            const globalNs = localStorage.getItem('profile_name_sharing');
            updated.fullName = globalNs ? globalNs === 'full' : true;
            return updated;
          });

          // Restore aboutMe from global defaults
          if (globalFields) {
            setAboutMeEnabled(globalFields.includes('aboutMe'));
          }
          // Hometown defaults ON when set — the coverage win. Not gated on
          // globalFields: pre-feature defaults never carried currentCity.
          setHometownEnabled(!!data.currentCity);

          // Flag that this is a new connection with no prior shares
          setIsFirstShare(true);
        }
      } catch (error) {
        console.error('[SharingSelection] Error loading profile:', error);
      }
    };

    loadProfile();
  }, [chatRoomId, selectionMode]);

  // Lock body scroll when overlay is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  // Toggle field selection (multi-select mode)
  const toggleField = (fieldKey) => {
    setSelectedFields((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  // Select single contact (single-select mode) - persist globally for next connection
  const selectContact = (fieldKey) => {
    setSelectedContact(fieldKey);
    localStorage.setItem('profile_default_contact', fieldKey);
  };

  // Handle contact row click based on selection mode
  const handleContactClick = (fieldKey) => {
    if (selectionMode === 'single') {
      selectContact(fieldKey);
      // Collapse back to initial view after selecting
      if (isEditMode) {
        setIsEditMode(false);
      }
    } else {
      toggleField(fieldKey);
    }
  };

  // Check if a contact is selected (works for both modes)
  const isContactSelected = (fieldKey) => {
    if (selectionMode === 'single') {
      return selectedContact === fieldKey;
    }
    return selectedFields[fieldKey];
  };

  // Handle Done button - share and close immediately
  const handleDone = async () => {
    console.log(
      '[SharingSelection] Done clicked with mode:',
      selectionMode,
      'selectedContact:',
      selectedContact,
      'aboutMeEnabled:',
      aboutMeEnabled
    );

    // Build sharedFields array based on selection mode
    const sharedFields = ['photoUrl']; // Always share photo

    if (selectionMode === 'single') {
      // Single-select mode: include selectedContact + aboutMe if enabled
      if (selectedContact && profileData && profileData[selectedContact]) {
        sharedFields.push(selectedContact);
      }
      if (aboutMeEnabled && hasAboutMeContent(profileData)) {
        sharedFields.push('aboutMe');
      }
    } else {
      // Multi-select mode: include all selected fields that have values
      Object.entries(selectedFields).forEach(([fieldKey, isSelected]) => {
        // fullName and aboutMe are toggles, not contact fields — skip here
        if (fieldKey === 'fullName' || fieldKey === 'aboutMe') return;
        if (isSelected && profileData && profileData[fieldKey]) {
          sharedFields.push(fieldKey);
        }
      });
      // About Me is a separate toggle (like name), not a contact checkbox
      if (aboutMeEnabled && hasAboutMeContent(profileData)) {
        sharedFields.push('aboutMe');
      }
    }

    // Hometown — its own default-on field, independent of mode and contact selection
    if (hometownEnabled && profileData?.currentCity) {
      sharedFields.push('currentCity');
    }

    // Determine full name sharing — single-select uses toggle state, multi-select uses selectedFields
    const isFullName = selectionMode === 'single' ? nameSharingFull : selectedFields.fullName;

    // Commit via the shared sharing path (also used by the inline SHARED list).
    const result = await commitSharedFields({
      chatRoomId,
      sharedFields,
      isFullName,
      profileData,
      connectionData,
      updateContactSharingStatus,
      shareMethod,
    });
    if (!result) {
      onClose();
      return;
    }
    const { sharedContactTypes } = result;

    // Set sessionStorage flag to show prompt chips on chat screen
    sessionStorage.setItem(`prompt_chips_active_${chatRoomId}`, 'true');

    if (onComplete) {
      // onComplete owns closing — do NOT also call onClose. On the
      // fresh-exchange page onClose means "back a step to the share sheet",
      // and a completed share must close everything (device pass 2,
      // 2026-07-19: submit posted the messages AND re-raised the sheet).
      onComplete(chatRoomId, { sharedContactTypes });
    } else {
      onClose();
    }
  };

  // Handle Skip — go straight to chat without sharing anything
  const handleSkip = () => {
    console.log('[SharingSelection] Skip clicked — nothing shared, going to chat');

    if (onComplete) {
      onComplete(chatRoomId, { sharedContactTypes: null });
    }
    onClose();
  };

  // Available shareable fields with display labels — shared logic (utils/sharingFields.js)
  const availableFields = getAvailableSharingFields(profileData);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={() => {
        if (isInitialShare) {
          handleSkip();
        } else {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      {/* Bottom sheet container */}
      <motion.div
        initial={{ y: 500 }}
        animate={{ y: 0 }}
        exit={{ y: 500 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto',
          backgroundColor: COLORS.WHITE,
          borderRadius: '24px 24px 0 0',
          position: 'relative',
          padding: '32px 24px 40px',
          boxShadow: SHADOWS.TIER3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* X — back out a step without sharing (device pass 9: the editor was
            a dead end; escaping lands on the composer Plus for single shares).
            Same semantics as the backdrop tap. */}
        <button
          aria-label="Close sharing editor"
          onClick={() => (isInitialShare ? handleSkip() : onClose())}
          style={{
            position: 'absolute',
            top: '14px',
            right: '14px',
            width: '40px',
            height: '40px',
            border: 'none',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <X size={20} color={COLORS.CHAT_TEXT_DARK} weight="regular" />
        </button>

        <AnimatePresence mode="wait">
          {/* Contact selection step */}
          <motion.div
            key="contact-step"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {/* "Nothing shared yet" note — new connection with pre-checked defaults */}
            {isFirstShare && (
              <div
                style={{
                  width: '100%',
                  textAlign: 'center',
                  marginBottom: '8px',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    color: '#999',
                    fontWeight: '400',
                  }}
                >
                  Nothing shared yet
                </span>
              </div>
            )}

            {/* Header - shown in multi-select initial share mode only */}
            {selectionMode === 'multi' && isInitialShare && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  marginBottom: '16px',
                }}
              >
                <span
                  style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: '#333',
                  }}
                >
                  {headerText || (isEditMode ? 'Select contacts' : 'Sharing')}
                </span>
              </div>
            )}

            {/* Full name toggle — single-select initial share only */}
            {selectionMode === 'single' && (
              <div
                onClick={() => setNameSharingFull(!nameSharingFull)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: nameSharingFull ? '#F0F0F0' : '#F8F8F8',
                  borderRadius: BORDER_RADIUS.PILL,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: '400',
                    fontStyle: nameSharingFull ? 'normal' : 'italic',
                    color: nameSharingFull ? '#5A5A5A' : '#BEBEBE',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {nameSharingFull
                    ? profileData?.name || ''
                    : profileData?.name?.split(' ')[0] || ''}
                </span>
              </div>
            )}

            {/* Contact selection */}
            {selectionMode === 'single' ? (
              /* Single-select: Collapsed = pill, Expanded = inline list (no duplication) */
              <div style={{ width: '100%', position: 'relative' }}>
                {/* Contact pill — always visible, tap to toggle list */}
                <div
                  onClick={() => setIsEditMode(!isEditMode)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: selectedContact ? '#F0F0F0' : '#F8F8F8',
                    borderRadius: BORDER_RADIUS.PILL,
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {(() => {
                      const selectedField = availableFields.find((f) => f.key === selectedContact);
                      if (selectedField) {
                        const Icon = selectedField.icon;
                        return (
                          <>
                            <Icon size={20} color="#5A5A5A" weight="regular" />
                            <span
                              style={{
                                fontSize: '16px',
                                fontWeight: '400',
                                color: '#5A5A5A',
                              }}
                            >
                              {selectedField.label}
                            </span>
                          </>
                        );
                      }
                      // No contact selected — unselected styling
                      return (
                        <span
                          style={{
                            fontSize: '16px',
                            fontStyle: 'italic',
                            color: '#BEBEBE',
                          }}
                        >
                          Contact
                        </span>
                      );
                    })()}
                  </div>
                  <CaretUpDown size={18} color="#999999" weight="bold" />
                </div>

                {/* Liquid glass dropdown — floats above the pill */}
                <AnimatePresence>
                  {isEditMode && (
                    <>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          zIndex: 50,
                        }}
                        onClick={() => setIsEditMode(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.92 }}
                        transition={{ duration: 0.2, ease: [0.2, 0.6, 0.35, 1] }}
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          right: 0,
                          width: '220px',
                          marginBottom: '8px',
                          maxHeight: '400px',
                          overflowY: 'auto',
                          WebkitOverflowScrolling: 'touch',
                          backgroundColor: 'rgba(255, 255, 255, 0.65)',
                          WebkitBackdropFilter: 'blur(60px) saturate(200%)',
                          backdropFilter: 'blur(60px) saturate(200%)',
                          borderRadius: '16px',
                          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
                          zIndex: 51,
                        }}
                      >
                        {availableFields.map(({ key, icon: Icon, label }) => (
                          <button
                            key={key}
                            onClick={() => handleContactClick(key)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              padding: '15px 18px',
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                              gap: '14px',
                              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.05)',
                            }}
                          >
                            <Icon
                              size={22}
                              color="#333"
                              weight="regular"
                              style={{
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                fontSize: '20px',
                                fontWeight: 400,
                                color: '#1c1c1e',
                                lineHeight: '26px',
                              }}
                            >
                              {label}
                            </span>
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            setSelectedContact(null);
                            localStorage.removeItem('profile_default_contact');
                            setIsEditMode(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            padding: '15px 18px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            gap: '14px',
                            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.05)',
                          }}
                        >
                          <XCircle
                            size={22}
                            color="#999"
                            weight="regular"
                            style={{
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: '20px',
                              fontWeight: 400,
                              color: '#999',
                              lineHeight: '26px',
                            }}
                          >
                            None
                          </span>
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              /* Multi-select mode */
              <AnimatePresence mode="popLayout" initial={false}>
                {!isEditMode ? (
                  <motion.div
                    key="condensed-mode"
                    layout="position"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                    style={{ width: '100%' }}
                  >
                    <div
                      onClick={() => setIsEditMode(true)}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '12px 16px',
                        backgroundColor: '#F0F0F0',
                        borderRadius: '24px',
                        cursor: 'pointer',
                        width: '100%',
                      }}
                    >
                      {(() => {
                        const selectedList = availableFields.filter((f) => selectedFields[f.key]);
                        const iconsToShow = selectedList.slice(0, 3);
                        const remaining = selectedList.length - 3;
                        return (
                          <>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '12px',
                              }}
                            >
                              {iconsToShow.map((field) => {
                                const Icon = field.icon;
                                return (
                                  <Icon
                                    key={field.key}
                                    size={24}
                                    color="#AAAAAA"
                                    weight="regular"
                                  />
                                );
                              })}
                            </div>
                            {remaining > 0 && (
                              <span
                                style={{
                                  position: 'absolute',
                                  right: '28px',
                                  fontSize: '15px',
                                  fontWeight: '400',
                                  color: '#AAAAAA',
                                }}
                              >
                                +{remaining}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="expanded-mode"
                    layout="position"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                    style={{ position: 'relative', width: '100%', marginBottom: '16px' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        width: '100%',
                        maxHeight: 'calc(55vh - 180px)',
                        overflowY: 'auto',
                        paddingBottom: '8px',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      }}
                    >
                      {availableFields.length === 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '24px 16px',
                            color: '#999',
                            fontSize: '15px',
                            textAlign: 'center',
                          }}
                        >
                          You&apos;ve shared all your contacts
                        </div>
                      ) : (
                        <>
                          {/* Full name toggle — single name has no toggle */}
                          {profileData?.name?.includes(' ') ? (
                            <SelectionRow
                              key="fullName"
                              icon={null}
                              label={
                                selectedFields.fullName
                                  ? profileData?.name || 'Full name'
                                  : profileData?.name?.split(' ')[0] || 'First name'
                              }
                              isSelected={selectedFields.fullName}
                              onClick={() => toggleField('fullName')}
                              showCheckmark={true}
                            />
                          ) : (
                            <SelectionRow
                              key="fullName"
                              icon={null}
                              label={profileData?.name || 'Name'}
                              isSelected={true}
                              showCheckmark={true}
                            />
                          )}
                          {/* Hometown toggle — its own default-on field, above About me */}
                          {profileData?.currentCity && (
                            <SelectionRow
                              key="hometown"
                              icon={MapPin}
                              label="Hometown"
                              isSelected={hometownEnabled}
                              onClick={() => setHometownEnabled(!hometownEnabled)}
                              showCheckmark={true}
                            />
                          )}
                          {/* About me toggle — like name, not a contact */}
                          {hasAboutMeContent(profileData) && (
                            <SelectionRow
                              key="aboutMe"
                              icon={User}
                              label="About me"
                              isSelected={aboutMeEnabled}
                              onClick={() => setAboutMeEnabled(!aboutMeEnabled)}
                              showCheckmark={true}
                            />
                          )}
                          {availableFields.map(({ key, icon, label }) => (
                            <SelectionRow
                              key={key}
                              icon={icon}
                              label={label}
                              isSelected={isContactSelected(key)}
                              onClick={() => handleContactClick(key)}
                              showCheckmark={true}
                            />
                          ))}
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            {/* Hometown row — its own default-on field, above About me */}
            {selectionMode === 'single' && profileData?.currentCity && (
              <InlineToggleRow
                icon={MapPin}
                iconWeight="fill"
                label="Hometown"
                enabled={hometownEnabled}
                onToggle={() => setHometownEnabled(!hometownEnabled)}
              />
            )}

            {/* About me row — below contact selection */}
            {selectionMode === 'single' && hasAboutMeContent(profileData) && (
              <InlineToggleRow
                icon={User}
                label="About me"
                enabled={aboutMeEnabled}
                onToggle={() => setAboutMeEnabled(!aboutMeEnabled)}
              />
            )}

            {/* Footer — Share */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: '8px',
                marginTop: '8px',
              }}
            >
              {/* Clear/Select all toggle pill - shown in multi-select expanded mode with >3 contacts */}
              {isEditMode && selectionMode === 'multi' && availableFields.length > 3 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F0F0F0',
                    borderRadius: '24px',
                    width: '100%',
                    padding: '12px 16px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    // Toggleable keys: contacts only (not fullName or aboutMe)
                    const toggleKeys = availableFields.map((f) => f.key);
                    const selectedCount = toggleKeys.filter((key) => selectedFields[key]).length;
                    if (selectedCount > 0) {
                      // Clear all — reset contacts, name to full, aboutMe off
                      const updated = { ...selectedFields };
                      toggleKeys.forEach((key) => {
                        updated[key] = false;
                      });
                      updated.fullName = true;
                      setSelectedFields(updated);
                      setAboutMeEnabled(false);
                      setHometownEnabled(false);
                    } else {
                      // Select all contacts
                      const updated = { ...selectedFields };
                      toggleKeys.forEach((key) => {
                        updated[key] = true;
                      });
                      setSelectedFields(updated);
                    }
                  }}
                >
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: '400',
                      color: '#9A9A9A',
                    }}
                  >
                    {(() => {
                      const toggleKeys = availableFields.map((f) => f.key);
                      const count = toggleKeys.filter((key) => selectedFields[key]).length;
                      return count > 0 ? 'Clear all' : 'Select all';
                    })()}
                  </span>
                </div>
              )}

              {/* Action button - "Share" for initial share, "Update" for share more */}
              <ButtonRounded
                text={buttonText || (isInitialShare ? 'Share' : 'Update')}
                onClick={handleDone}
                size="full"
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default SharingSelectionOverlay;
